import { useMutation } from "@apollo/client";
import { Navigate, useOutletContext, useSearchParams } from "react-router-dom";

import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { GET_CAREER_PLAN_HUB_QUERY, GET_CAREER_PLAN_QUERY } from "../../queries";
import { UPSERT_EXPERIENCE_INDEX_ASSESSMENT_ANSWERS_MUTATION } from "../../mutations";
import ExperienceIndexAnswersTab from "../_ExperienceIndexAnswersTab";
import type { CareerProfileQueryResult } from "../_useCareerProfileQuery";
import { careerPlanCompetencyIdFromSearch, careerProfileExperienceIndexHref } from "../careerPlanLatest";

export default function CareerProfileExperienceIndexAssessmentPage() {
  const [searchParams] = useSearchParams();
  const { pgaId, assessment, competencies, canManageCareerPlan } = useOutletContext<CareerProfileQueryResult>();
  const competencyId = careerPlanCompetencyIdFromSearch(searchParams);
  const [upsertAnswers, { loading: isSaving }] = useMutation(UPSERT_EXPERIENCE_INDEX_ASSESSMENT_ANSWERS_MUTATION, {
    refetchQueries: pgaId
      ? [
          { query: GET_CAREER_PLAN_QUERY, variables: { pgaId } },
          { query: GET_CAREER_PLAN_HUB_QUERY, variables: { pgaId } }
        ]
      : [],
    awaitRefetchQueries: true
  });

  if (!pgaId) return null;
  const personPgaId = pgaId;
  if (!competencyId) {
    return <Navigate to={careerProfileExperienceIndexHref(personPgaId)} replace />;
  }

  async function saveAnswers(savedCompetencyId: string, answers: { questionId: string; optionId: string }[]) {
    try {
      const { data: mutationData, errors } = await upsertAnswers({
        variables: {
          input: {
            pgaId: personPgaId,
            competencies: [{ competencyId: savedCompetencyId, answers }]
          }
        }
      });

      if (errors?.length) {
        showErrorSnackbar(errors[0]?.message || "Failed to save Experience Index answers");
        return false;
      }

      const payload = mutationData?.upsertExperienceIndexAssessmentAnswers;
      if (!payload?.success) {
        showErrorSnackbar(payload?.message || "Failed to save Experience Index answers");
        return false;
      }

      showSnackbar("Experience Index answers saved");
      return true;
    } catch (saveError) {
      showErrorSnackbar(saveError instanceof Error ? saveError.message : "Failed to save Experience Index answers");
      return false;
    }
  }

  return (
    <ExperienceIndexAnswersTab
      key={competencyId}
      answers={assessment?.answers ?? []}
      canEdit={Boolean(canManageCareerPlan)}
      competencies={competencies}
      focusedCompetencyId={competencyId}
      isSaving={isSaving}
      onSave={saveAnswers}
    />
  );
}
